import { Box, Flex } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { v4 as uuid } from 'uuid';
import { SidebarContent } from 'src/UI/app-container/SidebarContent';
import { ClientStep } from './new-steps/ClientStep';
import { AdviceStep } from './new-steps/AdviceStep';
import { FlowHeader } from 'src/UI/generate-flow/FlowHeader';
import { TemplateStep } from './new-steps/TemplateStep';
import { FeeDocumentsStep } from './new-steps/FeeDocumentsStep';
import { PillButton } from 'src/UI/generate-flow/PillButton';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { SearchPill } from 'src/UI/generate-flow/SearchPill';
import { AppendicesStep } from './new-steps/SupportDocumentationStep';
import { NewSuitabilityData, StepId, StepProps } from './types';
import { SiestaBarLayout } from 'src/UI/app-container/sidebar/SiestaBarLayout';
import { SuitabilitySidebar } from './edit/components/SuitabilitySidebar';
import { ProcessingStep } from './new-steps/ProcessingStep';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useContentMeta } from 'src/UI/app-container';
import { ChatMode } from 'src/UI/app-container/chat/types';
import { ReportFormatSwitch } from './components/ReportFormatSwitch';

const initialData = (flowId: string): NewSuitabilityData => ({
  flowId,
  client: undefined,
  adviceFiles: [],
  optimiseAdviceReadability: true,
  template: undefined,
  reportFormat: 'REPORT',
  feeDocuments: [],
  supportDocuments: [],
  highlightedElement: undefined,
  isFamily: false,
});

type StepDef = {
  id: StepId;
  title: string;
  disabledButtonlabel: string;
  render: (props: StepProps) => React.ReactNode;
  canContinue?: (data: NewSuitabilityData) => boolean;
  canFilter?: boolean;
};

export default function NewSuitabilityFlowPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const backHref = location.state?.fromPath ?? `/clients/all`;

  const { client: currentClient, clientId } = useCurrentClient();
  const preselectFamilyId = location.state?.familyId;
  useContentMeta({ pageType: 'client', entityId: clientId, chatMode: ChatMode.CLIENT });

  const flowId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('flowId');
    if (fromUrl) return fromUrl;
    const fromState = location.state?.flowId;
    if (fromState) return fromState;
    const id = uuid();
    const url = new URL(window.location.href);
    url.searchParams.set('flowId', id);
    window.history.replaceState(null, '', url);
    return id;
  }, [location.state?.flowId]);

  const storageKey = `suitability-flow-${flowId}`;

  const [data, setData] = useState<NewSuitabilityData>(() => {
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return initialData(flowId);
      }
    }
    return initialData(flowId);
  });

  const [stepIndex, setStepIndex] = useState(() => {
    const saved = sessionStorage.getItem(`${storageKey}-step`);
    return saved ? parseInt(saved, 10) : 0;
  });

  const [filter, setFilter] = useState('');

  // Persist state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(data));
  }, [data, storageKey]);

  useEffect(() => {
    sessionStorage.setItem(`${storageKey}-step`, String(stepIndex));
  }, [stepIndex, storageKey]);
  const [isReportSubmitting, setIsReportSubmitting] = useState(false);

  useEffect(() => {
    setFilter('');
  }, [stepIndex]);

  const steps: StepDef[] = useMemo(
    () => [
      {
        id: 'client',
        title: data.isFamily ? 'Select family' : 'Select client',
        disabledButtonlabel: data.isFamily ? 'Select the family' : 'Select the client',
        canContinue: d => (d.isFamily ? !!d.family?.id : !!d.client?.id),
        canFilter: true,
        render: props => (
          <ClientStep
            {...props}
            preselectClientId={currentClient?.id}
            preselectFamilyId={preselectFamilyId}
          />
        ),
      },
      {
        id: 'advice',
        title: 'Select advice',
        disabledButtonlabel: 'Select documents',
        canContinue: () => true,
        canFilter: true,
        render: props => <AdviceStep {...props} />,
      },
      {
        id: 'documents',
        title: 'Select documents',
        disabledButtonlabel: 'Select documents',
        canContinue: () => true,
        canFilter: true,
        render: props => <FeeDocumentsStep {...props} />,
      },
      {
        id: 'template',
        title: 'Select template',
        disabledButtonlabel: 'Select template and continue',
        canContinue: d => !!d.template,
        render: props => <TemplateStep {...props} />,
      },
      {
        id: 'support',
        title: 'Select appendices',
        disabledButtonlabel: 'Select documents',
        canContinue: () => true,
        canFilter: true,
        render: props => <AppendicesStep {...props} />,
      },
      {
        id: 'processing',
        title: 'Processing',
        disabledButtonlabel: 'Processing...',
        canContinue: () => true,
        canFilter: false,
        render: props => <ProcessingStep {...props} onSubmittingChange={setIsReportSubmitting} />,
      },
    ],
    [data.isFamily, currentClient?.id, preselectFamilyId],
  );

  const current = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const canContinue = (current.canContinue ? current.canContinue(data) : true) && !data.isLoading;

  const canFilter = current.canFilter ?? false;
  const placeholder = useMemo(() => {
    if (current.id !== 'client') return 'Search';

    return data.isFamily ? 'Search family by name/date' : 'Search client by name/date';
  }, [current.id, data.isFamily]);

  const resetCurrentStepSelection = useCallback(() => {
    setData(prev => {
      switch (current.id) {
        case 'advice':
          return { ...prev, adviceFiles: [] };

        case 'template':
          return { ...prev, template: undefined };

        case 'documents':
          return { ...prev, feeDocuments: [] };

        case 'support':
          return { ...prev, supportDocuments: [] };

        default:
          return prev;
      }
    });
  }, [current.id, setData]);

  const goBack = useCallback(() => {
    if (!isFirst) setStepIndex(i => i - 1);
    resetCurrentStepSelection();
  }, [isFirst, resetCurrentStepSelection]);

  const goNext = useCallback(() => {
    if (!isLast) setStepIndex(i => i + 1);
  }, [isLast]);

  const clearFlowStorage = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem(`${storageKey}-step`);
  }, [storageKey]);

  const onClose = useCallback(() => {
    clearFlowStorage();
    navigate(backHref);
  }, [navigate, backHref, clearFlowStorage]);

  const disabledButtonLabel = current.disabledButtonlabel;
  const stepLabelMap: Partial<Record<StepId, string>> = {
    support: 'Generate report',
  };
  const continueLabel = !canContinue
    ? disabledButtonLabel
    : (stepLabelMap[current.id] ?? 'Continue');

  return (
    <>
      <Flex direction="column" h="100%" pos="relative">
        <FlowLayout
          header={
            !isLast ? (
              <FlowHeader onClose={onClose} onBack={stepIndex > 0 ? goBack : undefined} />
            ) : undefined
          }
          body={
            current.id === 'processing' ? (
              <Box px="md" flex={1} mih={0} h="100%">
                {current.render({
                  data,
                  setData,
                  onNext: goNext,
                  filter,
                })}
              </Box>
            ) : (
              <FlowBody
                footerAlign="center"
                footer={
                  <Flex direction="column" w={360}>
                    {current.id === 'template' && data.template?.id && (
                      <Flex justify="center">
                        <ReportFormatSwitch
                          value={data.reportFormat}
                          onChange={reportFormat => setData(prev => ({ ...prev, reportFormat }))}
                        />
                      </Flex>
                    )}

                    <PillButton
                      fullWidth
                      disabled={!canContinue}
                      label={continueLabel}
                      onClick={goNext}
                    />
                  </Flex>
                }
              >
                {current.render({
                  data,
                  setData,
                  onNext: goNext,
                  filter,
                  searchBar: canFilter ? (
                    <SearchPill value={filter} onChange={setFilter} placeholder={placeholder} />
                  ) : undefined,
                })}
              </FlowBody>
            )
          }
        />
      </Flex>
      <SidebarContent>
        <SiestaBarLayout>
          <SuitabilitySidebar
            stepIndex={stepIndex}
            steps={steps}
            data={data}
            setData={setData}
            currentStepId={current.id}
            isReportSubmitting={isReportSubmitting}
          />
        </SiestaBarLayout>
      </SidebarContent>
    </>
  );
}
